import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

import { signInAsAdmin } from '../admin/sign-in';
import { ApiError } from '../api/client';
import { getAuthMethods, startGoogleAuth } from '../api/endpoints/signup';
import type { LoginResponse } from '../api/schemas';
import { getDeviceId } from '../auth/deviceId';
import { useSession } from '../auth/useSession';
import { useWhatsAppLogin, whatsappLinkNotice, type WhatsAppLogin } from '../auth/useWhatsAppLogin';
import { ErrorBanner } from '../components/ErrorBanner';
import { FormField } from '../components/FormField';
import { Notice } from '../components/Notice';
import { QrCode } from '../features/agents/reception/QrCode';

export function EntrarPage() {
  const { login, adoptLogin } = useSession();
  const navigate = useNavigate();
  // Whether this deployment has self-serve signup open at all. A plain effect
  // rather than react-query: the login page renders outside the authenticated
  // shell, and it should not need a QueryClient to draw a form. A failed probe
  // just means no link, never a broken login page.
  const [signupOpen, setSignupOpen] = useState(false);
  const [googleOpen, setGoogleOpen] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getAuthMethods()
      .then((methods) => {
        if (cancelled) return;
        setSignupOpen(methods.data.signup);
        setGoogleOpen(methods.data.google);
        setWhatsappOpen(methods.data.whatsapp === true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const whatsapp = useWhatsAppLogin({
    deviceId: getDeviceId,
    onSignedIn: (result: LoginResponse) => {
      adoptLogin?.(result);
      void navigate({ to: result.mustChangePassword ? '/cambiar-contrasena' : '/' });
    },
  });

  // The callback lands back here with a reason when Google could not sign the
  // person in; read once from the URL rather than held in state.
  const googleError = googleErrorMessage(
    new URLSearchParams(window.location.search).get('error'),
  );

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(undefined);
    try {
      // A WhatsApp the person just proved rides along, and this login links it.
      const link = whatsapp.link;
      const result = link
        ? await login(identifier, password, getDeviceId(), link)
        : await login(identifier, password, getDeviceId());
      if (link) {
        whatsapp.forgetLink();
        const notice = whatsappLinkNotice(result);
        if (notice) (notice.ok ? toast.success : toast)(notice.text);
      }
      await navigate({ to: result.mustChangePassword ? '/cambiar-contrasena' : '/' });
    } catch (err) {
      const admin = isWrongCredentials(err) ? await signInAsAdmin(identifier, password).catch(() => undefined) : undefined;
      if (admin) {
        await navigate({ to: admin.mustChangePassword ? '/admin/cambiar-contrasena' : '/admin' });
      } else {
        setError(loginErrorMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if ((whatsapp.phase === 'waiting' || whatsapp.phase === 'signingIn') && whatsapp.message) {
    return <WhatsAppWaiting whatsapp={whatsapp} />;
  }

  const notLinked = whatsapp.phase === 'notLinked';
  const shownError = error ?? whatsapp.error;

  return (
    <main className="auth">
      <div className="auth-card">
        <Brand />
        <h1>Entra a tu negocio</h1>
        <p className="lede">
          {notLinked ? 'Una sola vez con tu usuario y contraseña.' : 'Ingresa con tu usuario y contraseña.'}
        </p>
        {notLinked ? (
          <Notice>
            <span className="wa-login-notice">
              <WhatsAppGlyph />
              <span>
                Ese WhatsApp todavía no está en tu cuenta. Entra una vez con tu usuario y contraseña y queda
                vinculado para la próxima.
              </span>
            </span>
          </Notice>
        ) : null}
        {shownError ? <ErrorBanner message={shownError} /> : null}
        {!shownError && googleError ? <ErrorBanner message={googleError} /> : null}
        <form onSubmit={handleSubmit}>
          <FormField label="Usuario" htmlFor="username">
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Contraseña" htmlFor="password">
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </FormField>
          <button type="submit" className="btn" disabled={submitting}>
            {submitting ? 'Entrando…' : 'Entrar'}
          </button>
        </form>
        {notLinked ? (
          <button type="button" className="btn ghost wa-login-back" onClick={whatsapp.forgetLink}>
            Usar otro WhatsApp
          </button>
        ) : null}
        {whatsappOpen && !notLinked ? (
          <button
            type="button"
            className="btn secondary auth-whatsapp"
            disabled={whatsapp.phase === 'starting'}
            onClick={() => void whatsapp.start()}
          >
            <WhatsAppGlyph />
            {whatsapp.phase === 'starting' ? 'Pidiendo el código…' : 'Entrar con WhatsApp'}
          </button>
        ) : null}
        {googleOpen ? (
          <button
            type="button"
            className="btn secondary auth-google"
            onClick={() => startGoogleAuth('login')}
          >
            Continuar con Google
          </button>
        ) : null}
        {signupOpen ? (
          <p className="auth-alt">
            ¿Primera vez? <Link to="/registro">Crea tu negocio</Link>
          </p>
        ) : null}
      </div>
    </main>
  );
}

/**
 * The message to send, ready in WhatsApp with one tap, and as a QR for a
 * person whose WhatsApp lives on the phone while this page is on a computer.
 */
function WhatsAppWaiting({ whatsapp }: { whatsapp: WhatsAppLogin }) {
  const message = whatsapp.message!;
  const beforeCode = message.code ? message.text.slice(0, message.text.lastIndexOf(message.code)) : message.text;
  const signingIn = whatsapp.phase === 'signingIn';
  return (
    <main className="auth">
      <div className="auth-card">
        <Brand />
        <h1>Entra con tu WhatsApp</h1>
        <p className="lede">
          Manda este mensaje a Dale desde el WhatsApp de tu cuenta. Entras solo en cuanto llegue.
        </p>
        <div className="wa-login-message">
          <p>
            {beforeCode}
            {message.code ? <strong className="wa-login-code">{message.code}</strong> : null}
          </p>
        </div>
        <a className="btn wa-login-open" href={message.whatsappUrl} target="_blank" rel="noopener noreferrer">
          <WhatsAppGlyph />
          Abrir WhatsApp
        </a>
        <div className="wa-login-qr">
          <QrCode value={message.whatsappUrl} alt="Código QR con el mensaje para Dale" />
          <p>¿Tu WhatsApp está en el teléfono? Escanea este código con la cámara y manda el mensaje.</p>
        </div>
        <p className="wa-login-wait" role="status">
          <span className="wa-login-dot" aria-hidden="true" />
          {signingIn ? 'Llegó. Entrando…' : (whatsapp.error ?? 'Esperando tu mensaje…')}
        </p>
        <button type="button" className="btn ghost wa-login-back" disabled={signingIn} onClick={whatsapp.cancel}>
          Usar usuario y contraseña
        </button>
      </div>
    </main>
  );
}

function Brand() {
  return (
    <p className="brand" aria-hidden="true">
      <span className="brand-word">
        Dale<span className="dot">.</span>
      </span>
      <span className="brand-sub">Negocios</span>
    </p>
  );
}

function WhatsAppGlyph() {
  return (
    <svg className="wa-glyph" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"
      />
    </svg>
  );
}

/** The callback's `?error=` reasons, in the product's own voice. */
function googleErrorMessage(reason: string | null): string | undefined {
  if (reason === 'google_no_account') {
    return 'Esa cuenta de Google no está vinculada a ningún negocio. Entra con tu usuario y vincúlala desde Negocio.';
  }
  if (reason === 'google_unverified') return 'Google no confirmó ese correo.';
  if (reason === 'google_taken') return 'Esa cuenta de Google ya está vinculada a otro acceso.';
  if (reason === 'google_failed') return 'No pudimos completar el ingreso con Google.';
  return undefined;
}

/** Only a plain wrong password tries the HQ next: a Dale operator signs in here too. */
function isWrongCredentials(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * A 401 always reads as one fixed line, never the backend's own message: the
 * flow spec calls for no enumeration of which field was wrong (docs/FLOWS.md
 * §2). A 429 similarly gets a fixed line regardless of body shape, since rate
 * limiting may not return a JSON error envelope at all. A 423 means the
 * account itself is frozen from too many failed attempts, distinct from a
 * plain wrong-credentials 401.
 */
function loginErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'Usuario o contraseña incorrectos.';
    if (error.status === 423) return 'Cuenta bloqueada por intentos fallidos. Intenta de nuevo en 15 minutos.';
    if (error.status === 429) return 'Demasiados intentos. Espera un momento.';
    return error.message;
  }
  return 'No pudimos completar eso ahora mismo.';
}
